import { createForm, useCreateFormStore } from '@components/form';
import { RelativeSpinner } from '@components/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';

import { useMoveDocumentTemplate } from 'api/documents/hooks';
import { DocumentTemplate } from 'api/documents/requests';
import { useWorkspaces } from 'api/workspaces/hooks';

export const useMoveDocumentTemplateModal = () => useModal(MoveDocumentTemplateModal);

type MoveDocumentTemplateData = {
  workspace: string;
};

const Form = createForm<MoveDocumentTemplateData>();

export const MoveDocumentTemplateModal = ({
  documentTemplate,
}: {
  documentTemplate: DocumentTemplate;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const moveMutation = useMoveDocumentTemplate(documentTemplate.shortId);
  const { data: workspaces } = useWorkspaces();

  const handleSubmit = async (data: MoveDocumentTemplateData) => {
    await moveMutation.mutateAsync(data.workspace);
    hideModal();
  };

  if (!workspaces) {
    return <RelativeSpinner />;
  }

  return (
    <Modal>
      <Form store={formStore} onSubmit={handleSubmit}>
        <Modal.SimpleHeader title={t('title')} />
        <Modal.Content>
          <Stack space={6}>
            <Form.Select
              id="workspace"
              label={t('workspace')}
              options={workspaces
                .filter((workspace) => workspace.shortId !== documentTemplate.workspace)
                .map((workspace) => ({
                  value: workspace.shortId,
                  label: workspace.title,
                }))}
            />
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Form.Actions>
            <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideModal} />
            <Form.Submit
              text={t('confirm')}
              variant="primary-blue"
              disabled={moveMutation.isLoading}
            />
          </Form.Actions>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Move document',
    workspace: 'Workspace',
    confirm: 'Move document',
    cancel: 'Cancel',
    notPublishedDisabled: 'Cannot include travellers unless a journey is published',
  },
};
